import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMediaQuery, Grid } from '@material-ui/core';
import LatestNewCard from '../LatestNews/components/LatestNewsCard';
import Hero from './components/Hero';
import SimpleSelect from '../../components/molecules/SelectSimple/SimpleSelect';
import clsx from 'clsx';
import SectionWithArrows from 'components/organisms/SectionWithArrows/SectionWithArrows';
import { ReportsService } from 'services/ReportsService';
import { ReportsContext } from './contexts';
import { useQueryParams, NumberParam, withDefault } from 'use-query-params';
import { useTranslation } from 'react-i18next';
import Pagination from '@material-ui/lab/Pagination';

const useStyles = makeStyles((theme) => ({
  root: {},
  pagination: {
    marginTop: theme.spacing(8),
    display: 'flex',
    justifyContent: 'center',
  },
  filterContainer: {
    marginBottom: theme.spacing(4),
  },
  content: {
    paddingTop: theme.spacing(2),
  },
  reportSelected: {
    backgroundColor: '#ECEBEB9D',
  },
}));

const numberOfReportsPerPage = 5;
const reportsService = new ReportsService();

const Reports = (props) => {
  const { className, ...rest } = props; // eslint-disable-line no-unused-vars
  const classes = useStyles();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const { t } = useTranslation();
  const [years, setYears] = useState([]);
  const [reports, setReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const [reportsPerPage, setReportsPerPage] = useState(null);
  const [filters, setFilters] = useQueryParams({
    year: NumberParam,
    category: withDefault(NumberParam, 1),
    id: NumberParam,
  });

  useEffect(() => {
    if (!filters.category) return;

    (async () => {
      const { data } = await reportsService.reports(filters.category, filters.year);

      setReports(data);
    })();
  }, [filters]);

  useEffect(() => {
    if (!filters.category) return;
    (async () => {
      const { data: years } = await reportsService.years(filters.category);

      setYears(years);
    })();
  }, [filters.category]);

  useEffect(() => {
    if (reports.length > 0) {
      setTotalPages(Math.ceil(reports.length / numberOfReportsPerPage));
      setReportsPerPage(reports.slice(0, numberOfReportsPerPage));
    }
  }, [reports]);

  const handleFilterChange = (name) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [name]: event.target.value,
    }));
  };

  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    setReportsPerPage(
      reports.slice((page - 1) * numberOfReportsPerPage, (page - 1) * numberOfReportsPerPage + numberOfReportsPerPage),
    );
  };

  return (
    <ReportsContext.Provider value={{ filters, setFilters }}>
      <div className={clsx(className)}>
        <Hero />
        <SectionWithArrows propClasses={{ section: classes.content }}>
          <div className={classes.filterContainer}>
            <SimpleSelect
              name='year'
              placeholder={t('COMMON.SELECT_YEAR')}
              startText={t('COMMON.SELECTED_YEAR')}
              items={years}
              value={filters.year}
              onChange={handleFilterChange('year')}
              secondStyle={true}
            />
          </div>
          <Grid container spacing={isMd ? 4 : 2}>
            {reportsPerPage?.map((item, index) => {
              const { date, ...otherItemProps } = item;
              return (
                <Grid
                  item
                  xs={12}
                  key={index}
                  style={{ paddingBottom: 10, paddingTop: 10 }}
                  className={clsx({
                    [classes.reportSelected]: item.id === filters.id,
                  })}
                >
                  <LatestNewCard
                    anchorScroll={item.id === filters.id}
                    item={otherItemProps}
                    buttonText={t('COMMON.DOWNLOAD')}
                    showMoreButton={true}
                    showMoreButtonText={t('COMMON.SHOW_MORE')}
                    showLessButtonText={t('COMMON.SHOW_LESS')}
                  />
                </Grid>
              );
            })}
          </Grid>
          <div className={clsx(classes.pagination)}>
            <Pagination count={totalPages} page={currentPage} size='large' onChange={handlePageChange} />
          </div>
        </SectionWithArrows>
      </div>
    </ReportsContext.Provider>
  );
};

Reports.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
};

export default Reports;
